import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    FiArrowLeft,
    FiZap,
    FiShield,
    FiTruck,
    FiTag,
    FiBox,
    FiAward,
    FiHelpCircle,
    FiChevronDown,
    FiMapPin
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import PkrIcon from '../components/common/PkrIcon';
import FeaturedProductsSection from '../components/common/FeaturedProductsSection';
import { BUSINESS_WHATSAPP_CHAT_URL } from '../config/businessInfo';
import './CashPage.css';

const SHOWROOM_IMG = '/assets/media/images/shop/IMG_5702.HEIC_20261022225000.jpg';

const CASH_FAQS = [
    {
        q: 'Is Cash on Delivery (COD) available for orders?',
        a: 'No, Cash on Delivery (COD) is not available. All orders are processed via Advance Bank Transfer or paid directly at our flagship showroom in Sanda Kalan, Lahore.'
    },
    {
        q: 'Are cash prices discounted compared to normal retail?',
        a: 'Yes, because there are zero financing overheads, all cash buyers receive immediate factory-direct discounts of 5% to 10% compared to typical retail prices.'
    },
    {
        q: 'Will I get an official printed receipt and warranty card?',
        a: 'Every single cash purchase includes an official Rahber Corporation computer-generated sales invoice along with the brand’s original stamped warranty card valid nationwide.'
    },
    {
        q: 'Can I test the appliance before taking it home from the showroom?',
        a: 'Absolutely! At our Main Bazar Sanda Kalan showroom, our technicians will gladly unbox, power on, and demonstrate any refrigerator, LED TV, or deep freezer before you make payment.'
    }
];

const CashPage = () => {
    const navigate = useNavigate();
    const [openFaq, setOpenFaq] = useState(0);

    const handleWhatsappDeal = () => {
        const text = 'Hello Rahber Corporation! I am looking for the best cash deal on an appliance with direct discount and fast Lahore delivery.';
        window.open(`${BUSINESS_WHATSAPP_CHAT_URL}?text=${encodeURIComponent(text)}`, '_blank');
    };

    return (
        <div className="cash-page">
            {/* Rich Hero Header with Integrated Back Navigation & Cash Perks */}
            <header className="cash-hero-premium">
                <div className="container">
                    <div style={{ marginBottom: '1rem', textAlign: 'left' }}>
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="product-page-back-btn"
                            aria-label="Go back"
                        >
                            <FiArrowLeft /> Back
                        </button>
                    </div>

                    <div className="cash-hero-badge">
                        <PkrIcon aria-hidden="true" /> Wholesale Direct Pricing &amp; Instant Savings
                    </div>
                    <h1 className="cash-hero-title">
                        Buy on Direct Cash &amp; Unlock <span className="highlight-text">Maximum Discounts</span>
                    </h1>
                    <p className="cash-hero-desc">
                        Enjoy wholesale-level pricing on brand-packaged factory-sealed home appliances. Zero processing delay, direct ownership, and reliable doorstep delivery across Lahore.
                    </p>

                    <div className="cash-perks-strip">
                        <div className="cash-perk-item">
                            <FiTag className="perk-icon" />
                            <div>
                                <span className="perk-title">Instant 5% - 10% Off</span>
                                <span className="perk-sub">Direct factory-margin savings</span>
                            </div>
                        </div>
                        <div className="cash-perk-divider" />
                        <div className="cash-perk-item">
                            <FiZap className="perk-icon" />
                            <div>
                                <span className="perk-title">Instant Ownership</span>
                                <span className="perk-sub">No paperwork, no waiting</span>
                            </div>
                        </div>
                        <div className="cash-perk-divider" />
                        <div className="cash-perk-item">
                            <FiBox className="perk-icon" />
                            <div>
                                <span className="perk-title">Sealed Original Box</span>
                                <span className="perk-sub">Official brand warranty cards</span>
                            </div>
                        </div>
                        <div className="cash-perk-divider" />
                        <div className="cash-perk-item">
                            <FiTruck className="perk-icon" />
                            <div>
                                <span className="perk-title">Prompt Lahore Delivery</span>
                                <span className="perk-sub">Doorstep inspection allowed</span>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Why Cash at Rahber Corporation */}
            <section className="cash-benefits-section">
                <div className="container">
                    <div className="section-header text-center">
                        <span className="section-eyebrow">EXCLUSIVE PERKS</span>
                        <h2 className="section-title">Why Cash Buyers Choose Rahber</h2>
                        <p className="section-subtitle">
                            We pass our massive dealer network savings directly on to you with full guarantee of authenticity.
                        </p>
                    </div>

                    <div className="cash-benefits-grid">
                        <div className="cash-card">
                            <div className="cash-card-icon-wrap">
                                <FiTag />
                            </div>
                            <h3>Guaranteed Best Cash Prices</h3>
                            <p>
                                By eliminating financing risk and admin overhead, we offer the lowest transparent pricing on top brands like Dawlance, PEL, Orient, Gree, and TCL.
                            </p>
                            <span className="cash-highlight-pill">Wholesale Advantage</span>
                        </div>

                        <div className="cash-card">
                            <div className="cash-card-icon-wrap">
                                <FiZap />
                            </div>
                            <h3>Zero Formalities or Delays</h3>
                            <p>
                                No CNIC photocopies, no background verifications, and no reference checks. Simply choose your appliance and take it home immediately.
                            </p>
                            <span className="cash-highlight-pill">Immediate Handover</span>
                        </div>

                        <div className="cash-card">
                            <div className="cash-card-icon-wrap">
                                <FiAward />
                            </div>
                            <h3>Official Brand Warranty Cards</h3>
                            <p>
                                Every item is factory packaged with official manufacturer warranty cards included for peace of mind.
                            </p>
                            <span className="cash-highlight-pill">Warranty Card Inside</span>
                        </div>

                        <div className="cash-card">
                            <div className="cash-card-icon-wrap">
                                <FiTruck />
                            </div>
                            <h3>Prompt Safe Home Delivery</h3>
                            <p>
                                Fast 24-hour delivery across Lahore. Our logistics team handles heavy refrigerators and AC units with safety blankets and careful loading.
                            </p>
                            <span className="cash-highlight-pill">Careful Handling</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* How Cash Purchase Works */}
            <section className="cash-how-section">
                <div className="container">
                    <div className="cash-how-box">
                        <div className="how-content-col">
                            <span className="section-eyebrow">EFFORTLESS 3 STEPS</span>
                            <h2>How to Order on Cash</h2>
                            <p className="how-lead">
                                Whether you prefer ordering online from the comfort of your home or walking into our Lahore showroom, buying on cash is quick and seamless.
                            </p>

                            <div className="how-timeline">
                                <div className="timeline-item">
                                    <div className="timeline-num">1</div>
                                    <div className="timeline-details">
                                        <h4>Pick Your Product &amp; Add to Cart</h4>
                                        <p>Browse our catalog, select your required size or tonnage, and click &ldquo;Add to Cart&rdquo;.</p>
                                    </div>
                                </div>

                                <div className="timeline-item">
                                    <div className="timeline-num">2</div>
                                    <div className="timeline-details">
                                        <h4>Enter Delivery Address &amp; Confirm</h4>
                                        <p>Provide your delivery location in Lahore or Punjab and select Bank Transfer or Showroom Pickup.</p>
                                    </div>
                                </div>

                                <div className="timeline-item">
                                    <div className="timeline-num">3</div>
                                    <div className="timeline-details">
                                        <h4>Order Verification &amp; Prompt Dispatch</h4>
                                        <p>Our showroom team verifies payment and dispatches your company-sealed appliance with stamped warranty cards.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="how-actions">
                                <Link to="/products" className="btn-cash-primary">
                                    Browse Cash Products Now
                                </Link>
                                <button
                                    type="button"
                                    onClick={handleWhatsappDeal}
                                    className="btn-cash-whatsapp"
                                >
                                    <FaWhatsapp /> Ask for Today&apos;s Best Cash Deal
                                </button>
                            </div>
                        </div>

                        {/* Right Showroom Snapshot */}
                        <div className="how-media-col">
                            <div className="how-img-frame">
                                <img
                                    src={SHOWROOM_IMG}
                                    alt="Rahber Corporation Showroom"
                                    className="how-img"
                                    loading="lazy"
                                />
                                <div className="how-caption-bar">
                                    <FiMapPin className="how-caption-pin" />
                                    <div className="how-caption-text">
                                        <strong>Visit In-Person Showroom</strong>
                                        <span>Main Bazar Chandni Chowk, Sanda Kalan, Lahore</span>
                                    </div>
                                </div>
                            </div>

                            <div className="how-trust-box">
                                <FiShield className="trust-shield-icon" />
                                <div>
                                    <h5>Direct Wholesale Supply</h5>
                                    <p>Trusted electronics retail and wholesale supply serving homes and shops in Lahore.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Cash Products Grid */}
            <section className="cash-products-section">
                <div className="container">
                    <FeaturedProductsSection
                        channel="cash"
                        title="Hot Deals for Cash Buyers"
                        subtitle="Immediate stock available for same-day dispatch or in-store pickup."
                    />
                </div>
            </section>

            {/* Cash FAQs */}
            <section className="cash-faq-section">
                <div className="container">
                    <div className="section-header text-center">
                        <span className="section-eyebrow">COMMON QUERIES</span>
                        <h2 className="section-title">Cash Purchase FAQs</h2>
                        <p className="section-subtitle">
                            Everything you need to know about payments, invoices, and delivery.
                        </p>
                    </div>

                    <div className="faq-accordion-container">
                        {CASH_FAQS.map((item, idx) => {
                            const isOpen = openFaq === idx;
                            return (
                                <div
                                    key={item.q}
                                    className={`faq-accordion-item ${isOpen ? 'active' : ''}`}
                                >
                                    <button
                                        type="button"
                                        className="faq-accordion-header"
                                        onClick={() => setOpenFaq(isOpen ? -1 : idx)}
                                        aria-expanded={isOpen}
                                    >
                                        <span className="faq-question">
                                            <FiHelpCircle className="faq-q-icon" />
                                            {item.q}
                                        </span>
                                        <FiChevronDown className={`faq-chevron ${isOpen ? 'rotate' : ''}`} />
                                    </button>
                                    {isOpen && (
                                        <div className="faq-accordion-body">
                                            <p>{item.a}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CashPage;
